/**
 * SHELL — screen router. Mounts one screen at a time into the shell layer over
 * the Render canvas, handling teardown and back-stack.
 */

import { clear } from './dom';

export interface Screen {
  readonly node: HTMLElement;
  /** Optional cleanup run when the screen is replaced. */
  dispose?(): void;
}

export type ScreenFactory = () => Screen;

export interface Router {
  /** Replace the current screen. */
  show(factory: ScreenFactory): void;
  /** Current screen's root node (or null). */
  readonly current: HTMLElement | null;
  dispose(): void;
}

export function createRouter(host: HTMLElement): Router {
  let active: Screen | null = null;

  return {
    show(factory) {
      active?.dispose?.();
      clear(host);
      active = factory();
      host.appendChild(active.node);
      // Move focus to the new screen for accessibility / keyboard users.
      const focusable = active.node.querySelector<HTMLElement>('[autofocus], button, [href], input, [tabindex]');
      focusable?.focus({ preventScroll: true });
    },
    get current() {
      return active?.node ?? null;
    },
    dispose() {
      active?.dispose?.();
      active = null;
      clear(host);
    },
  };
}
